import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useIdleTimeout from '../hooks/useIdleTimeout';
import Login from './Login';
import Signup from './Signup';
import Home from './Home';
import Dashboard from './Dashboard';
import Subscription from './Subscription';
import DiarioObraHome from './diarioObras/diarioObraHome';
import DiarioObraDetalhesHome from './diarioObras/diarioObrasDetalhes/diarioObraDetalhesHome';
import OrcamentosList from './OrcamentosList';
import ConfiguracaoConta from './ConfiguracaoConta';
import CadastrarCliente from './CadastrarCliente';
import ControleCustos from '../pages/ControleCustos';
import ControleDiarias from '../pages/ControleDiarias';
import ProtectedRoute from './ProtectedRoute';
import EmailVerificationRoute from './EmailVerificationRoute';
import RedefinirSenha from './RedefinirSenha';
import VerificarEmail from '../pages/VerificarEmail';

const AppRoutes = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Estado para controlar a exibição de login ou cadastro
  const [showSignup, setShowSignup] = useState(false);

  // Sempre chamamos o hook, mas ele só ativa a funcionalidade quando currentUser existe
  useIdleTimeout(currentUser);

  // Effect para verificar role do usuário em cada mudança de rota
  useEffect(() => {
    if (currentUser && currentUser.role === 'inativo') {
      navigate('/subscription', { replace: true });
    }
  }, [currentUser, location.pathname, navigate]);

  // Manipuladores de eventos para alternar entre login e cadastro
  const handleShowSignup = () => {
    setShowSignup(true);
  };
  
  const handleShowLogin = () => {
    setShowSignup(false);
  };

  // Componente personalizado para a rota de login/signup
  const AuthPage = () => {
    return showSignup 
      ? <Signup onBackToLogin={handleShowLogin} /> 
      : <Login onRegisterClick={handleShowSignup} />;
  };

  // Função para determinar o redirecionamento inicial
  const getInitialRedirect = () => {
    if (!currentUser) {
      return "/login";
    }
    if (currentUser.role === 'inativo') {
      return "/subscription";
    }
    return "/home";
  };

  // Componente para verificar autenticação e role
  const AuthCheck = ({ children }) => {
    if (!currentUser) {
      return <Navigate to="/login" replace />;
    }
    if (currentUser.role === 'inativo') {
      return <Navigate to="/subscription" replace />;
    }
    return children;
  };

  return (
    <Routes>
      {/* Rotas públicas */}
      <Route path="/login" element={!currentUser ? <AuthPage /> : <Navigate to={getInitialRedirect()} replace />} />
      <Route path="/subscription" element={<Subscription />} />
      <Route path="/recuperar-senha" element={<RedefinirSenha />} />
      <Route path="/verificar-email" element={<VerificarEmail />} />

      {/* Rotas protegidas que não requerem email verificado */}
      <Route path="/home" element={
        <AuthCheck>
          <Home />
        </AuthCheck>
      } />
      <Route path="/dashboard" element={
        <AuthCheck>
          <Dashboard />
        </AuthCheck>
      } />
      <Route path="/configuracao-conta" element={
        <AuthCheck>
          <ConfiguracaoConta />
        </AuthCheck>
      } />

      {/* Rotas que requerem email verificado */}
      <Route path="/diario-obra" element={
        <EmailVerificationRoute>
          <DiarioObraHome />
        </EmailVerificationRoute>
      } />
      <Route path="/diario-obra/:id" element={
        <EmailVerificationRoute>
          <DiarioObraDetalhesHome />
        </EmailVerificationRoute>
      } />
      <Route path="/orcamentos" element={
        <EmailVerificationRoute>
          <OrcamentosList />
        </EmailVerificationRoute>
      } />
      <Route path="/orcamento/:cliente" element={
        <EmailVerificationRoute>
          <Dashboard />
        </EmailVerificationRoute>
      } />
      <Route path="/cadastrar-cliente" element={
        <EmailVerificationRoute>
          <CadastrarCliente />
        </EmailVerificationRoute>
      } />
      <Route path="/controle-custos" element={
        <EmailVerificationRoute>
          <ControleCustos />
        </EmailVerificationRoute>
      } />
      <Route path="/controle-diarias" element={
        <EmailVerificationRoute>
          <ControleDiarias />
        </EmailVerificationRoute>
      } />

      {/* Rota padrão */}
      <Route path="*" element={<Navigate to={getInitialRedirect()} replace />} />
    </Routes>
  );
};

export default AppRoutes; 